import { FormikContextType } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { ISelectOption, ISpruceFieldProps, onInputChangeSelectList, Select, SpruceField } from '../../ui-toolkit';
import { INewOrderSchema } from './validation/NewOrderSchema';
import { Show } from '../shared/Show';
import { UUID } from '../../types/shared/UUID';
import { labelComparator } from '../../utilities/labelComparator';

interface IClientInformationForm {
  autoFocus: boolean;
  clientId?: string;
  canSelectClient: boolean;
  onClientSelected: (x?: string) => void;
  clientCompanyList: ISelectOption<string>[];
  lenderCompanyList: ISelectOption<string>[];
  setSelectedClientId: React.Dispatch<React.SetStateAction<UUID | undefined>>;
  isNewOrder?: boolean;
}

const onChangeToState = (value: ISelectOption<string>) => value.value;

export const ClientInformationForm: React.FC<IClientInformationForm> = ({
  autoFocus,
  clientId,
  onClientSelected,
  lenderCompanyList,
  clientCompanyList,
  canSelectClient,
  setSelectedClientId,
  isNewOrder,
}) => {
  const [companyList, setList] = useState(clientCompanyList);
  const onInputChange = onInputChangeSelectList(clientCompanyList, setList);

  const lenderFieldName = isNewOrder ? 'lenderCompanyId' : 'lender_id';
  const clientFieldName = isNewOrder ? 'clientCompanyId' : 'client_id';

  const onSelect = async (form: FormikContextType<INewOrderSchema>, value: ISelectOption<string> | null) => {
    if (value && value.value) {
      form.setFieldTouched(clientFieldName);
      form.setFieldValue(clientFieldName, onChangeToState(value));
      setSelectedClientId(onChangeToState(value));

      const matchedCompany = lenderCompanyList.find((t) => t.label === value.label);
      if (matchedCompany) {
        form.setFieldTouched(lenderFieldName);
        form.setFieldValue(lenderFieldName, onChangeToState(matchedCompany));
      } else {
        form.setFieldTouched(lenderFieldName);
        form.setFieldValue(lenderFieldName, null);
      }
    } else {
      form.setFieldValue(clientFieldName, null);
      setSelectedClientId(undefined);
      setList(clientCompanyList);
    }
  };

  const transformValueToSelect = (value: any) =>
    companyList ? companyList.find((t: ISelectOption<string>) => t.value === value) : null;

  useEffect(() => {
    onClientSelected(clientId);
  }, [clientId, onClientSelected]);

  const sortedOptions = useMemo(() => companyList.toSorted(labelComparator), [companyList]);
  return (
    <Show when={canSelectClient}>
      <SpruceField name={clientFieldName}>
        {({ field, formik }: ISpruceFieldProps) => (
          <Select
            {...field}
            value={transformValueToSelect(field.value)}
            backspaceRemovesValue
            options={sortedOptions}
            isClearable
            onInputChange={onInputChange}
            onChange={(value) => onSelect(formik, value)}
            isRequired
            placeholder={`Select Client`}
            autoFocus={autoFocus}
            data-test-id={field.name}
            name={field.name}
            label={'Client'}
          />
        )}
      </SpruceField>
    </Show>
  );
};
